'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

const FEATURES = [
  {
    emoji: '🗺️',
    title: 'Explore the City',
    desc: 'Discover beaches, temples, restaurants, museums and hidden gems across Chennai.',
    color: 'from-orange-400 to-amber-400',
    bg: 'bg-orange-50',
    border: 'border-orange-100',
  },
  {
    emoji: '🚌',
    title: 'Multi-Modal Transit',
    desc: 'Plan routes using MTC buses, Chennai Metro, and local transport — all in one place.',
    color: 'from-blue-400 to-cyan-400',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
  },
  {
    emoji: '👤',
    title: 'Your Profile',
    desc: 'Save your interests, track visited places, and personalise your Chennai experience.',
    color: 'from-purple-400 to-pink-400',
    bg: 'bg-purple-50',
    border: 'border-purple-100',
  },
  {
    emoji: '📻',
    title: 'Live FM Radio',
    desc: 'Tune into Chennai\'s local FM stations while you explore the city.',
    color: 'from-green-400 to-teal-400',
    bg: 'bg-green-50',
    border: 'border-green-100',
  },
];

export default function LandingPage() {
  const router = useRouter();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase?.auth?.getUser();
        setIsLoggedIn(!!user);
      } catch {
        setIsLoggedIn(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFBF5] font-sans">
      {/* ── Sticky Nav ── */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-amber-100' : 'bg-transparent'
        }`}
      >
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AppLogo size={34} />
            <span className="font-extrabold text-gray-900 text-lg tracking-tight">Explore Chennai</span>
          </div>
          <div className="flex items-center gap-2">
            {!checkingAuth && (
              isLoggedIn ? (
                <Link
                  href="/profile"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-all shadow-sm"
                >
                  <span>👤</span> My Profile
                </Link>
              ) : (
                <Link
                  href="/signin"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-all shadow-sm"
                >
                  Sign In
                </Link>
              )
            )}
          </div>
        </div>
      </nav>
      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/images/chennai_signin_bg.png"
            alt="Aerial view of Chennai city with Marina Beach at golden hour"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 50%, rgba(255,251,245,1) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-5 pt-20 pb-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Live city data · Updated daily
          </div>
          <h1 className="text-5xl sm:text-6xl font-extrabold text-white leading-tight drop-shadow-xl mb-4">
            Explore Chennai
          </h1>
          <p className="text-xl text-white/90 font-medium drop-shadow mb-8 max-w-xl mx-auto">
            Discover places, plan transit routes, and make Chennai yours — anywhere, anytime.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {!checkingAuth && !isLoggedIn && (
              <Link
                href="/signin"
                className="flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-800 font-bold text-base hover:bg-white active:scale-95 transition-all shadow-xl border border-white/60"
              >
                👤 Sign In / Register
              </Link>
            )}
            {!checkingAuth && isLoggedIn && (
              <Link
                href="/profile"
                className="flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-800 font-bold text-base hover:bg-white active:scale-95 transition-all shadow-xl border border-white/60"
              >
                👤 My Profile
              </Link>
            )}
          </div>
        </div>
      </section>
      {/* ── Features Section ── */}
      <section className="max-w-5xl mx-auto px-5 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Everything you need to explore Chennai</h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto">From transit planning to discovering hidden gems — all in one app.</p>
        </div>

        {/* Bento-style asymmetric grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Large card — Explore */}
          <div className={`${FEATURES?.[0]?.bg} border ${FEATURES?.[0]?.border} rounded-3xl p-6 sm:row-span-2 flex flex-col justify-between min-h-[220px] hover:shadow-md transition-shadow`}>
            <div>
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${FEATURES?.[0]?.color} flex items-center justify-center text-2xl shadow-sm mb-4`}>
                {FEATURES?.[0]?.emoji}
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">{FEATURES?.[0]?.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{FEATURES?.[0]?.desc}</p>
            </div>
          </div>

          {/* Transit card */}
          <div className={`${FEATURES?.[1]?.bg} border ${FEATURES?.[1]?.border} rounded-3xl p-6 flex flex-col justify-between hover:shadow-md transition-shadow`}>
            <div>
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${FEATURES?.[1]?.color} flex items-center justify-center text-2xl shadow-sm mb-3`}>
                {FEATURES?.[1]?.emoji}
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-1">{FEATURES?.[1]?.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{FEATURES?.[1]?.desc}</p>
            </div>
          </div>

          {/* Profile card */}
          <div className={`${FEATURES?.[2]?.bg} border ${FEATURES?.[2]?.border} rounded-3xl p-6 flex flex-col justify-between hover:shadow-md transition-shadow`}>
            <div>
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${FEATURES?.[2]?.color} flex items-center justify-center text-2xl shadow-sm mb-3`}>
                {FEATURES?.[2]?.emoji}
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-1">{FEATURES?.[2]?.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{FEATURES?.[2]?.desc}</p>
            </div>
            <Link
              href={isLoggedIn ? '/profile' : '/signin'}
              className="mt-4 inline-flex items-center gap-1.5 text-purple-600 font-bold text-sm hover:gap-2.5 transition-all"
            >
              {isLoggedIn ? 'View Profile' : 'Sign In to Access'} <span>→</span>
            </Link>
          </div>
        </div>

        {/* FM Radio wide card */}
        <div className={`mt-4 ${FEATURES?.[3]?.bg} border ${FEATURES?.[3]?.border} rounded-3xl p-6 flex items-center gap-5 hover:shadow-md transition-shadow`}>
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${FEATURES?.[3]?.color} flex items-center justify-center text-3xl shadow-sm flex-shrink-0`}>
            {FEATURES?.[3]?.emoji}
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-1">{FEATURES?.[3]?.title}</h3>
            <p className="text-gray-600 text-sm">{FEATURES?.[3]?.desc}</p>
          </div>
        </div>
      </section>
      {/* ── Profile Preview Section ── */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 border-y border-amber-100 py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="flex flex-col sm:flex-row items-center gap-10">
            {/* Left: text */}
            <div className="flex-1 text-center sm:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-4">
                👤 Your Profile
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3 leading-tight">
                Make Chennai<br />your own
              </h2>
              <p className="text-gray-600 text-base mb-6 leading-relaxed">
                Sign in to save your interests, track the places you visit, and get a personalised Chennai experience tailored just for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/signin"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-orange-500 text-white font-bold text-sm hover:bg-orange-600 active:scale-95 transition-all shadow-md"
                >
                  Create Account
                </Link>
                {isLoggedIn && (
                  <Link
                    href="/profile"
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white text-orange-600 font-bold text-sm hover:bg-orange-50 active:scale-95 transition-all shadow-md border border-orange-200"
                  >
                    Go to My Profile →
                  </Link>
                )}
              </div>
            </div>

            {/* Right: profile card preview */}
            <div className="flex-1 w-full max-w-sm">
              <div className="bg-white rounded-3xl shadow-xl border border-amber-100 overflow-hidden">
                {/* Card header */}
                <div className="bg-gradient-to-r from-orange-400 to-amber-400 px-5 py-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/30 flex items-center justify-center text-white font-extrabold text-lg">
                    NC
                  </div>
                  <div>
                    <div className="text-white font-extrabold text-base">Chennai Explorer User</div>
                    <div className="text-orange-100 text-xs">Member since 2026</div>
                  </div>
                </div>
                {/* Interests preview */}
                <div className="px-5 py-4 border-b border-amber-50">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Your Interests</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['Beaches', 'Temples', 'Street Food', 'Transit', 'Culture']?.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-100">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Recent activity preview */}
                <div className="px-5 py-4">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Recent Activity</div>
                  <div className="flex flex-col gap-2">
                    {[
                      { icon: '🏖️', name: 'Marina Beach', color: 'bg-blue-50 text-blue-700' },
                      { icon: '🛕', name: 'Kapaleeshwarar Temple', color: 'bg-amber-50 text-amber-700' },
                      { icon: '🍽️', name: 'Murugan Idli Shop', color: 'bg-red-50 text-red-700' },
                    ]?.map((place) => (
                      <div key={place?.name} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl ${place?.color} text-xs font-semibold`}>
                        <span>{place?.icon}</span>
                        <span>{place?.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ── CTA Section ── */}
      <section className="bg-gradient-to-br from-orange-500 to-amber-500 py-14">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-3">Ready to explore Chennai?</h2>
          <p className="text-orange-100 text-base mb-8">Join thousands of Chennaites discovering their city in a whole new way.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {!checkingAuth && !isLoggedIn && (
              <Link
                href="/signin"
                className="flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-800 font-bold text-base hover:bg-white active:scale-95 transition-all shadow-xl border border-white/60"
              >
                👤 Sign In / Register
              </Link>
            )}
            {!checkingAuth && isLoggedIn && (
              <Link
                href="/profile"
                className="flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-800 font-bold text-base hover:bg-white active:scale-95 transition-all shadow-xl border border-white/60"
              >
                👤 My Profile
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
